import { Injectable} from '@angular/core';
import * as ForgRockIdentityProvider from '@forgerock/javascript-sdk';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { SERVICE_END_POINT } from '../api-constants/service-endpoints.constants';
import { CURRENT_ENVIROMENT } from '../api-constants/enviroment';


export interface Config {
    am_url: string;
    api_url: string;
    journey_login: string;
    web_oauth_client: string;
    realm_path: string;
    isSlocRemoved: boolean;
    triggeredBy: string;
    isDateInputRequired: boolean; //added for date toggling b/w input and dropdown
    isSourceTerminated: boolean;
    terminationDate: string;
    dynatraceScriptUrl: string;
    locateApiKey: string;
    updatePwdLink: string;
    enableUpdatePwdLink: boolean;
    quoteCreationCheckDate: string;
    reCaptchaSiteKey: string;
    enableCfLogic: boolean;
  }


@Injectable({
  providedIn: 'root'
})
/**
 * Service to load and manage configuration settings.
 *
 * The `ConfigLoaderService` is responsible for loading configuration settings
 * from an environment-specific URL and initializing the configuration for the
 * `ForgRockIdentityProvider`.
 *
 * @remarks
 * This service uses `HttpClient` to fetch the configuration and sets it up
 * for the `ForgRockIdentityProvider`.
 *
 * @example
 * ```typescript
 * const configLoaderService = new ConfigLoaderService(handler);
 * configLoaderService.loadConfig().then(() => {
 *   const config = configLoaderService.configuration;
 *   // Use the loaded configuration
 * });
 * ```
 */
export class ConfigLoaderService {

  private readonly httpClient: HttpClient;
  private config: Config;

  constructor(handler: HttpBackend) {
    this.httpClient = new HttpClient(handler);
  }

  get configuration() {
    return this.config;
  }

  /**
   * Loads the configuration from the environment-specific URL.
   *
   * @returns {Promise<any>} A promise that resolves when the configuration is loaded and set.
   */
   public async loadConfig(): Promise<any> {

       const url = this.getConfigEndpoint();
       return this.httpClient.get<Config>(url).toPromise()
           .then((config: Config) => {
               this.config = config;
               this.fetchAndReplaceDomain();
               this.setConfig();
               this.injectDynatraceScript(this.config?.dynatraceScriptUrl);
           });
  }

  getConfigEndpoint() {
    let href = window.location.href;
    let queryString = href.split('?');
    let queryParam = new URLSearchParams((queryString && queryString.length > 0 ? queryString[1] : null));
    if (queryParam && queryParam.get('applcationRefNo')) {
      return SERVICE_END_POINT[CURRENT_ENVIROMENT.environment].envHOConfigUrl;
    } else  {
      return SERVICE_END_POINT[CURRENT_ENVIROMENT.environment].envConfigUrl;
    }
  }

  fetchAndReplaceDomain() {
    const hostname = window.location.hostname;
    if (hostname.includes('pp-1.pp') || hostname.includes('pp-2.pp')) {
      this.config.api_url = this.config.api_url.replace('.in.','.');
      this.config.updatePwdLink = this.config.updatePwdLink.replace('.in.','.');
    }
  }

  injectDynatraceScript(url: string) {
    if (!url) return;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.crossOrigin = 'anonymous';
    script.src = url;
    document.head.appendChild(script);
  }
 
    /**
     * Sets the configuration for the ForgRockIdentityProvider.
     * 
     * This method initializes the configuration settings required for the 
     * ForgRockIdentityProvider, including client ID, redirect URI, scope, 
     * server configuration, realm path, and login journey.
     * 
     * @private
     * @remarks
     * The configuration settings are derived from the `config` object.
     * 
     * - `clientId`: The OAuth client ID.
     * - `redirectUri`: The URI to redirect to after authentication.
     * - `scope`: The scope of the authentication request.
     * - `serverConfig`: Configuration for the server, including base URL and timeout.
     * - `realmPath`: The path to the authentication realm.
     * - `tree`: The login journey configuration.
     */
    private setConfig(): void {
        const baseUrl = this.config.am_url + '/am';
          ForgRockIdentityProvider.Config.set({
            clientId: this.config.web_oauth_client,
            redirectUri: this.config.api_url,
            scope: 'openid profile',
            serverConfig: {
              baseUrl,
              timeout: 20000 //90000
            },
            realmPath: this.config.realm_path,
            tree: this.config.journey_login
        });
      }  
}

